import TurndownService from 'turndown';

export interface ParsedArticle {
  title: string;
  subtitle: string;
  content: string;
  images: string[];
  publishedAt: number | undefined;
}

/**
 * Parse an exported Substack HTML file into structured article data.
 * Substack exports articles as standalone HTML files with a predictable structure.
 */
export function parseSubstackHtml(html: string): ParsedArticle {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Extract title from <h1> or meta or <title>
  const h1 = doc.querySelector('h1.post-title, h1');
  const title = h1?.textContent?.trim()
    ?? doc.querySelector('meta[property="og:title"]')?.getAttribute('content')
    ?? doc.querySelector('title')?.textContent?.trim()
    ?? 'Untitled';

  // Extract subtitle
  const subtitleEl = doc.querySelector('h3.subtitle, .subtitle');
  const subtitle = subtitleEl?.textContent?.trim() ?? '';

  // Extract published date
  let publishedAt: number | undefined;
  const dateEl = doc.querySelector('time');
  if (dateEl) {
    const dateStr = dateEl.getAttribute('datetime') ?? dateEl.textContent?.trim();
    if (dateStr) {
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) {
        publishedAt = Math.floor(parsed.getTime() / 1000);
      }
    }
  }

  // Find the main content body
  const bodyEl = doc.querySelector('.body.markup, .post-content, .available-content, article');
  const contentEl = bodyEl ?? doc.body;

  // Remove the title and subtitle from the content if present inside
  if (contentEl) {
    const innerH1 = contentEl.querySelector('h1.post-title, h1');
    if (innerH1 && innerH1.textContent?.trim() === title) {
      innerH1.remove();
    }
    const innerSub = contentEl.querySelector('h3.subtitle, .subtitle');
    if (innerSub && innerSub.textContent?.trim() === subtitle) {
      innerSub.remove();
    }
    // Remove subscribe buttons and footer
    contentEl.querySelectorAll('.subscription-widget-wrap, .subscribe-widget, .footer, .post-footer, .share-dialog').forEach(el => el.remove());
  }

  // Convert HTML to Markdown using Turndown
  const turndown = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
    hr: '---',
  });

  // Handle figures with images and captions
  turndown.addRule('figure', {
    filter: 'figure',
    replacement: (_content, node) => {
      const el = node as HTMLElement;
      const img = el.querySelector('img');
      const caption = el.querySelector('figcaption');
      if (img) {
        const src = img.getAttribute('src') ?? '';
        const alt = caption?.textContent?.trim() ?? img.getAttribute('alt') ?? '';
        return `\n\n![${alt}](${src})\n${caption ? `*${caption.textContent?.trim()}*` : ''}\n\n`;
      }
      return _content;
    },
  });

  // Handle Substack image divs
  turndown.addRule('substackImage', {
    filter: (node) => {
      return node.nodeName === 'DIV' && node.classList.contains('captioned-image-container');
    },
    replacement: (_content, node) => {
      const el = node as HTMLElement;
      const img = el.querySelector('img');
      const caption = el.querySelector('.image-caption');
      if (img) {
        const src = img.getAttribute('src') ?? '';
        const alt = caption?.textContent?.trim() ?? img.getAttribute('alt') ?? '';
        return `\n\n![${alt}](${src})\n${caption ? `*${caption.textContent?.trim()}*` : ''}\n\n`;
      }
      return _content;
    },
  });

  const markdown = turndown.turndown(contentEl?.innerHTML ?? '');

  // Extract all image URLs from the content
  const images: string[] = [];
  const imgElements = contentEl?.querySelectorAll('img') ?? [];
  imgElements.forEach((img) => {
    const src = img.getAttribute('src');
    if (src && src.startsWith('http')) {
      images.push(src);
    }
  });

  return {
    title,
    subtitle,
    content: cleanMarkdown(markdown),
    images,
    publishedAt,
  };
}

/** Clean up common markdown artifacts */
function cleanMarkdown(md: string): string {
  return md
    // Remove excessive blank lines (more than 2)
    .replace(/\n{4,}/g, '\n\n\n')
    // Clean up leading/trailing whitespace
    .trim();
}

/** Generate a URL-friendly slug from a title */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}
